import type { SchemaPathTree } from '@angular/forms/signals';
import { min, required } from '@angular/forms/signals';
import type { NewCapabilitySuggestion } from '@indutrust/capability-model';
import type { MachinePark } from '@indutrust/factory-model';
import type { MachineCatalogEntry } from '@indutrust/machine-model';

/** The editable fields of a machine park entry as a flat form value. */
export type MachineParkForm = {
  id: number | null;
  machine_catalog_id: number | null;
  name: string;
  brand: string;
  quantity: number | null;
  capability_ids: number[];
  /** Transient — dual-written on save; not persisted on {@link MachinePark}. */
  capability_suggestions: NewCapabilitySuggestion[];
};

function toQuantity(value: number | string | undefined | null): number | null {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Returns an initial {@link MachineParkForm} value, optionally seeded
 * from an existing {@link MachinePark}.
 */
export function createMachineParkFormValue(
  machine?: Partial<MachinePark>
): MachineParkForm {
  return {
    id: machine?.id ?? null,
    machine_catalog_id: machine?.machine_catalog_id ?? null,
    name: machine?.name ?? '',
    brand: machine?.brand ?? '',
    quantity: toQuantity(machine?.quantity),
    capability_ids: machine?.capability_ids ?? [],
    capability_suggestions: [],
  };
}

/**
 * Seeds a {@link MachineParkForm} from a shared machine-catalog entry the
 * supplier picked in the search step — denormalizes its name, brand, and
 * capability ids onto the machine-park draft.
 */
export function createMachineParkFormValueFromCatalogEntry(
  entry: MachineCatalogEntry
): MachineParkForm {
  return {
    ...createMachineParkFormValue(),
    machine_catalog_id: entry.id,
    name: entry.name,
    brand: entry.brand,
    capability_ids: entry.capability_ids,
  };
}

/**
 * Schema fragment applying machine-park validation rules.
 *
 * @example
 * const machineParkForm = form(model, machineParkFormSchema);
 */
export function machineParkFormSchema(
  p: SchemaPathTree<MachineParkForm>
): void {
  required(p.name);
  required(p.brand);
  required(p.quantity);
  min(p.quantity, 1);
}
